import ShowProductsButtonPresenter from "./show-products-button.js";

import ResponsiveFilterView from "../view/responsive-filter.js";
import ResponsiveFilterButtonView from "../view/responsive-filter-button.js";

import ResponsiveFilterModel from "../model/responsive-filter.js";

import {
  render,
  remove,
  RenderPosition
} from "../../../render/render.js";

import {
  debounce
} from "../../../../utils/debounce.js";

import {
  getMaxZIndex
} from "../../../../utils/get-max-z-index.js";

import {
  MOBILE_MEDIA_QUERY,
  defaultResponsiveFilterModelData,
  ActionType,
  KEABOARD_ESC_KEYS,
  RESPONSIVE_FILTER_TOUCH_THRESHOLD
} from "../../const.js";

import {
  RESPONSIVE_RESET_BUTTON_TEXT
} from "../lexicon.js";

import {
  AnimationClass
} from "../../../animation/const.js";

export default class ResponsiveFilter {
  constructor(
      filtersWrapperPresenter,
      animation,
      scrollToElement,
      changeFilter,
      initTotal
  ) {
    this._filtersWrapperPresenter = filtersWrapperPresenter;
    this._animation = animation;
    this._scrollToElement = scrollToElement;
    this._changeFilter = changeFilter;
    this._total = initTotal;

    this._showProductsButtonPresenter = null;

    this._responsiveFilterButtonView = null;
    this._responsiveFilterView = null;

    this._model = null;

    this._responsiveFilterWrapperSiblingElement = null;
    this._defaultResetButtonText = null;

    this._responsiveFilterButtonObserver = null;
    this._elementIntersection = {
      responsiveFilterButton: false,
      productListing: false
    };

    this._handleResponsiveFilterButtonClick = this._handleResponsiveFilterButtonClick.bind(this);
    this._handleWindowResizeButton = this._handleWindowResizeButton.bind(this);
    this._handleChangeModel = this._handleChangeModel.bind(this);
    this._handleChangeFilter = this._handleChangeFilter.bind(this);
    this._handleCloseResponsiveFilter = this._handleCloseResponsiveFilter.bind(this);
    this._handleEscKeyDown = this._handleEscKeyDown.bind(this);
    this._handleWindowResizeFilter = this._handleWindowResizeFilter.bind(this);
    this._handleWindowOrientationchangeFilter = this._handleWindowOrientationchangeFilter.bind(this);
    this._handleResponsiveFilterTouchstart = this._handleResponsiveFilterTouchstart.bind(this);
    this._handleResponsiveFilterTouchend = this._handleResponsiveFilterTouchend.bind(this);
    this._handleResponsiveFilterTouchcancel = this._handleResponsiveFilterTouchcancel.bind(this);
    this._handleShowProductsButtonClose = this._handleShowProductsButtonClose.bind(this);
    this._handleChangeFilterLoad = this._handleChangeFilterLoad.bind(this);
  }

  init() {
    this._responsiveFilterButtonView = new ResponsiveFilterButtonView();

    this._model = new ResponsiveFilterModel(Object.assign(
        {},
        defaultResponsiveFilterModelData,
        {
          total: this._total
        }
    ));
    this._model.addObserver(this._handleChangeModel);
    this._changeFilter.addLoadObserver(this._handleChangeFilterLoad);

    this._renderResponsiveButton();

    window.addEventListener(`resize`, debounce(this._handleWindowResizeButton), {
      passive: true
    });
  }

  _renderResponsiveFilter() {
    this._responsiveFilterView = new ResponsiveFilterView();

    const responsiveFilterElementZIndex = getMaxZIndex() + 10;
    this._responsiveFilterView.setZIndex(responsiveFilterElementZIndex);

    render(
        document.body,
        this._responsiveFilterView.getElement()
    );

    const filtersWrapperElement = this._filtersWrapperPresenter.getListingWrapperView().getFiltersWrapperElement();

    this._responsiveFilterWrapperSiblingElement = filtersWrapperElement.nextElementSibling;

    render(
        this._responsiveFilterView.getContainerElement(),
        filtersWrapperElement
    );

    this._filtersWrapperPresenter.getListingWrapperView().makeResponsible();

    this._filtersWrapperPresenter.setCheckboxesRelativeScrollContainer(this._responsiveFilterView.getElement());
    this._filtersWrapperPresenter.setResetRelativeScrollElements(
        this._responsiveFilterView.getElement(),
        this._responsiveFilterView.getCloseButtonWrapper()
    );

    this._responsiveFilterView.setCloseButtonClickHandler(this._handleCloseResponsiveFilter);

    document.addEventListener(`keydown`, this._handleEscKeyDown);
    window.addEventListener(`resize`, this._handleWindowResizeFilter, {
      passive: true
    });
    window.addEventListener(`orientationchange`, this._handleWindowOrientationchangeFilter);

    this._changeFilter.addLoadObserver(this._handleChangeFilter);

    this._responsiveFilterView.setTouchstartHandler(this._handleResponsiveFilterTouchstart);
    this._responsiveFilterView.setTouchendHandler(this._handleResponsiveFilterTouchend);
    this._responsiveFilterView.setTouchcancelHandler(this._handleResponsiveFilterTouchcancel);

    this._animation.fadeIn(
        this._responsiveFilterView.getElement()
    );

    this._showProductsButtonPresenter = new ShowProductsButtonPresenter(
        this._responsiveFilterView,
        this._changeFilter,
        this._model.getData().total
    );
    this._showProductsButtonPresenter.init();
    this._showProductsButtonPresenter.addCloseObserver(this._handleShowProductsButtonClose);

    this._defaultResetButtonText = this._filtersWrapperPresenter.getResetButtonText();
    this._filtersWrapperPresenter.setResetButtonText(RESPONSIVE_RESET_BUTTON_TEXT);

    render(
        this._responsiveFilterView.getHeadingContainerElement(),
        this._filtersWrapperPresenter.getResetButtonWrapper(),
        RenderPosition.AFTERBEGIN
    );

    if (this._filtersWrapperPresenter.getListingWrapperView().getSubcategoriesWrapperElement()) {
      render(
          this._responsiveFilterView.getContainerElement(),
          this._filtersWrapperPresenter.getListingWrapperView().getSubcategoriesWrapperElement(),
          RenderPosition.BEFOREBEGIN
      );
    }
  }

  _destroyResponsiveFilter() {
    this._changeFilter.removeLoadObserver(this._handleChangeFilter);

    document.removeEventListener(`keydown`, this._handleEscKeyDown);
    window.removeEventListener(`resize`, this._handleWindowResizeFilter);
    window.removeEventListener(`orientationchange`, this._handleWindowOrientationchangeFilter);

    this._filtersWrapperPresenter.getListingWrapperView().makeDefault();
    this._filtersWrapperPresenter.removeCheckboxesRelativeScrollContainer();
    this._filtersWrapperPresenter.removeResetRelativeScrollElements();

    this._animation.fadeOut(
        this._responsiveFilterView.getElement(),
        null,
        () => {
          render(
              this._responsiveFilterWrapperSiblingElement,
              this._filtersWrapperPresenter.getListingWrapperView().getFiltersWrapperElement(),
              RenderPosition.BEFOREBEGIN
          );

          this._filtersWrapperPresenter.setResetButtonText(this._defaultResetButtonText);
          this._defaultResetButtonText = null;

          render(
              this._filtersWrapperPresenter.getFormElement(),
              this._filtersWrapperPresenter.getResetButtonWrapper()
          );

          this._responsiveFilterWrapperSiblingElement = null;
          remove(this._responsiveFilterView);

          if (this._filtersWrapperPresenter.getListingWrapperView().getSubcategoriesWrapperElement()) {
            render(
                this._filtersWrapperPresenter.getListingWrapperView().getFiltersWrapperElement(),
                this._filtersWrapperPresenter.getListingWrapperView().getSubcategoriesWrapperElement(),
                RenderPosition.BEFOREBEGIN
            );
          }
        }
    );

    this._scrollToElement.scroll({
      element: this._filtersWrapperPresenter.getListingWrapperView().getElement()
    });

    this._showProductsButtonPresenter = null;
  }

  _renderResponsiveButton() {
    const modelData = this._model.getData();

    if (modelData.isButtonRendered || !window.matchMedia(MOBILE_MEDIA_QUERY).matches) {
      return;
    }

    this._model.changeData(Object.assign(
        {},
        modelData,
        {
          isButtonRendered: true
        }
    ), ActionType.RENDER_FILTER_BUTTON);
  }

  _removeResponsiveButton() {
    const modelData = this._model.getData();

    if (!modelData.isButtonRendered || window.matchMedia(MOBILE_MEDIA_QUERY).matches) {
      return;
    }

    this._model.changeData(Object.assign(
        {},
        modelData,
        {
          isButtonRendered: false
        }
    ), ActionType.REMOVE_FILTER_BUTTON);

    if (this._model.getData().isStickyButtonRendered === true) {
      this._model.changeData(Object.assign(
          {},
          this._model.getData(),
          {
            isStickyButtonRendered: false
          }
      ), ActionType.REMOVE_STICKY_FILTER_BUTTON);
    }
  }

  _observeResponsiveFilterButton() {
    const observerOptions = {
      root: null
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.target === this._filtersWrapperPresenter.getListingWrapperView().getProductsWrapperElement()) {
          this._elementIntersection.productListing = (entry.isIntersecting) ? true : false;
        }

        if (entry.target === this._responsiveFilterButtonView.getElement()) {
          this._elementIntersection.responsiveFilterButton = (entry.isIntersecting) ? true : false;
        }

        if (
          this._elementIntersection.responsiveFilterButton === false &&
          this._elementIntersection.productListing === true &&
          this._model.getData().isStickyButtonRendered === false
        ) {
          this._model.changeData(Object.assign(
              {},
              this._model.getData(),
              {
                isStickyButtonRendered: true
              }
          ), ActionType.RENDER_STICKY_FILTER_BUTTON);
        }

        if (
          this._model.getData().isStickyButtonRendered === true &&
          (
            this._elementIntersection.responsiveFilterButton === true ||
            this._elementIntersection.productListing === false
          )
        ) {
          this._model.changeData(Object.assign(
              {},
              this._model.getData(),
              {
                isStickyButtonRendered: false
              }
          ), ActionType.REMOVE_STICKY_FILTER_BUTTON);
        }
      });
    };

    this._responsiveFilterButtonObserver = new IntersectionObserver(observerCallback, observerOptions);

    this._responsiveFilterButtonObserver.observe(this._responsiveFilterButtonView.getElement());

    this._responsiveFilterButtonObserver.observe(this._filtersWrapperPresenter.getListingWrapperView().getProductsWrapperElement());
  }

  _unobserveResponsiveFilterButton() {
    this._responsiveFilterButtonObserver.unobserve(this._responsiveFilterButtonView.getElement());

    this._responsiveFilterButtonObserver.unobserve(this._filtersWrapperPresenter.getListingWrapperView().getProductsWrapperElement());
  }

  _processRenderStickyFilterButton() {
    const button = this._responsiveFilterButtonView.getStickyButtonElement();

    render(
        this._filtersWrapperPresenter.getListingWrapperView().getActionsElement(),
        button
    );
    this._animation.fadeIn(
        button,
        null,
        null,
        AnimationClass.SHOW_FLEX
    );
    this._responsiveFilterButtonView.setStickyButtonClickHandler(this._handleResponsiveFilterButtonClick);
  }

  _processDestroyStickyFilterButton() {
    this._animation.fadeOut(
        this._responsiveFilterButtonView.getStickyButtonElement(),
        null,
        () => {
          this._responsiveFilterButtonView.removeStickyButton();
        }
    );
  }

  _handleResponsiveFilterTouchstart(evt) {
    if (evt.touches.length !== 1) {
      return;
    }

    const touch = evt.changedTouches[0];

    this._model.changeData(Object.assign(
        {},
        this._model.getData(),
        {
          touchstartPageX: touch.pageX
        }
    ));
  }

  _handleResponsiveFilterTouchend(evt) {
    const modelData = this._model.getData();

    if (evt.changedTouches.length !== 1) {
      return;
    }

    const slidersElements = this._filtersWrapperPresenter.getSlidersElements();
    const inputsElements = this._filtersWrapperPresenter.getInputsElements();

    const isOverSlider = slidersElements.some((element) => {
      return element.contains(evt.target);
    });
    const isOverInput = inputsElements.some((element) => {
      return element.contains(evt.target);
    });

    const touch = evt.changedTouches[0];

    if (
      (modelData.touchstartPageX - touch.pageX > RESPONSIVE_FILTER_TOUCH_THRESHOLD) &&
      isOverSlider === false &&
      isOverInput === false
    ) {
      this._model.changeData(Object.assign(
          {},
          modelData,
          {
            touchstartPageX: null,
            isFilterRendered: false
          }
      ), ActionType.DESTROY_RESPONSIVE_FILTER);
    } else {
      this._model.changeData(Object.assign(
          {},
          modelData,
          {
            touchstartPageX: null
          }
      ));
    }
  }

  _handleResponsiveFilterTouchcancel() {
    this._model.changeData(Object.assign(
        {},
        this._model.getData(),
        {
          touchstartPageX: null
        }
    ));
  }

  _handleCloseResponsiveFilter() {
    const modelData = this._model.getData();

    if (!modelData.isFilterRendered) {
      return;
    }

    this._model.changeData(Object.assign(
        {},
        modelData,
        {
          isFilterRendered: false
        }
    ), ActionType.DESTROY_RESPONSIVE_FILTER);
  }

  _handleResponsiveFilterButtonClick() {
    const modelData = this._model.getData();
    const action = (modelData.isFilterRendered) ?
      ActionType.DESTROY_RESPONSIVE_FILTER :
      ActionType.RENDER_RESPONSIVE_FILTER;

    this._model.changeData(Object.assign(
        {},
        modelData,
        {
          isFilterRendered: !modelData.isFilterRendered
        }
    ), action);
  }

  _handleChangeFilter() {
    this._scrollToElement.scroll({
      element: this._filtersWrapperPresenter.getListingWrapperView().getElement()
    });
  }

  _handleWindowResizeButton() {
    if (window.matchMedia(MOBILE_MEDIA_QUERY).matches) {
      this._renderResponsiveButton();
    } else {
      this._removeResponsiveButton();
    }
  }

  _handleWindowResizeFilter() {
    if (!window.matchMedia(MOBILE_MEDIA_QUERY).matches) {
      const modelData = this._model.getData();

      if (!modelData.isFilterRendered) {
        return;
      }

      this._model.changeData(Object.assign(
          {},
          modelData,
          {
            isFilterRendered: false
          }
      ), ActionType.DESTROY_RESPONSIVE_FILTER);
    }
  }

  _handleWindowOrientationchangeFilter() {
    if (!window.matchMedia(MOBILE_MEDIA_QUERY).matches) {
      const modelData = this._model.getData();

      if (!modelData.isFilterRendered) {
        return;
      }

      this._model.changeData(Object.assign(
          {},
          modelData,
          {
            isFilterRendered: false
          }
      ), ActionType.DESTROY_RESPONSIVE_FILTER);
    }
  }

  _handleEscKeyDown(evt) {
    if (KEABOARD_ESC_KEYS.indexOf(evt.key) !== -1) {
      const modelData = this._model.getData();

      if (!modelData.isFilterRendered) {
        return;
      }

      this._model.changeData(Object.assign(
          {},
          modelData,
          {
            isFilterRendered: false
          }
      ), ActionType.DESTROY_RESPONSIVE_FILTER);
    }
  }

  _handleShowProductsButtonClose() {
    const modelData = this._model.getData();

    if (!modelData.isFilterRendered) {
      return;
    }

    this._model.changeData(Object.assign(
        {},
        modelData,
        {
          isFilterRendered: false
        }
    ), ActionType.DESTROY_RESPONSIVE_FILTER);
  }

  _handleChangeModel(action) {
    switch (action) {
      case ActionType.RENDER_FILTER_BUTTON:
        const button = this._responsiveFilterButtonView.getElement();

        render(
            this._filtersWrapperPresenter.getListingWrapperView().getActionsElement(),
            button
        );
        this._animation.fadeIn(
            button,
            null,
            null,
            AnimationClass.SHOW_FLEX
        );
        this._responsiveFilterButtonView.setClickHandler(this._handleResponsiveFilterButtonClick);
        this._observeResponsiveFilterButton();
        break;
      case ActionType.REMOVE_FILTER_BUTTON:
        remove(this._responsiveFilterButtonView);
        this._unobserveResponsiveFilterButton();
        break;
      case ActionType.RENDER_RESPONSIVE_FILTER:
        this._renderResponsiveFilter();
        break;
      case ActionType.DESTROY_RESPONSIVE_FILTER:
        this._destroyResponsiveFilter();
        break;
      case ActionType.RENDER_STICKY_FILTER_BUTTON:
        this._processRenderStickyFilterButton();
        break;
      case ActionType.REMOVE_STICKY_FILTER_BUTTON:
        this._processDestroyStickyFilterButton();
        break;
    }
  }

  _handleChangeFilterLoad(data) {
    this._model.changeData(Object.assign(
        {},
        this._model.getData(),
        {
          total: data.total
        }
    ));
  }
}
